"use client"

import { useState, useContext } from "react"
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Container,
  Card,
  CardContent,
  CardHeader,
  Typography,
  InputAdornment,
  Divider,
  alpha,
  useTheme,
  Alert,
  Fade,
  IconButton,
  Tooltip,
  Paper,
  Chip,
  Stack,
} from "@mui/material"
import {
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Label as LabelIcon,
  CreditCard as CreditCardIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  QrCode as QrCodeIcon,
} from "@mui/icons-material"
import axios from "axios"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { AppContext } from "../../contexts/AppContext"

function CardEditor({ idPartner, onCancel, onSave }) {
  const { state } = useContext(AppContext)
  const theme = useTheme()
  const [isGenerating, setIsGenerating] = useState(false)
  const [fidelityCode, setFidelityCode] = useState("")
  const [inputType, setInputType] = useState("phone")
  const [label, setLabel] = useState("")
  const [inputValue, setInputValue] = useState("")
  const [validationError, setValidationError] = useState(null)
  const queryClient = useQueryClient()

  const phoneNumberRegex = /^(?:\+213[5-7]\d{8}|0[5-7]\d{8})$/

  const generateEAN13 = async () => {
    const randomSequence = Array.from({ length: 12 }, () => Math.floor(Math.random() * 10))
    const checksum = calcEAN13Checksum(randomSequence)
    randomSequence.push(checksum)
    return randomSequence.join("")
  }

  const calcEAN13Checksum = (sequence) => {
    const sequence_sum = sequence.map((v, i) => (i % 2 === 0 ? v : v * 3)).reduce((sum, v) => sum + v, 0)
    return (10 - (sequence_sum % 10)) % 10
  }

  const handleGenerateFidelityCode = async () => {
    setIsGenerating(true)
    try {
      const code = await generateEAN13()
      setFidelityCode(code)
    } catch (error) {
      console.error("Error generating fidelity code:", error)
      alert("Failed to generate fidelity code")
    } finally {
      setIsGenerating(false)
    }
  }

  const mutation = useMutation({
    mutationFn: async (cardData) => {
      const response = await axios.post(`/api/v2/partner/${idPartner}/cards`, cardData, {
        headers: {
          Authorization: state.token ? `Bearer ${state.token}` : "",
          "Content-Type": "application/json",
        },
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["cards"])
      if (onSave) onSave()
    },
    onError: (error) => {
      console.error("Error adding card:", error)
      setValidationError("Failed to create card. Please try again.")
    },
  })

  const handleSubmit = () => {
    if (!inputValue) {
      setValidationError("Please enter a valid phone number or personal code.")
      return
    }
    if (inputType === "phone" && !phoneNumberRegex.test(inputValue)) {
      setValidationError(
        "Invalid phone number. Accepted formats: +2135XXXXXXXX, +2137XXXXXXXX, 05XXXXXXXX, or 07XXXXXXXX.",
      )
      return
    }
    if (!fidelityCode) {
      setValidationError("Please generate a fidelity code.")
      return
    }
    setValidationError(null)
    const cardData = {
      fidelity_code: String(fidelityCode),
      client_identification_type: inputType,
      label: label,
      ...(inputType === "phone" ? { client_phone_number: String(inputValue) } : { personal_code: String(inputValue) }),
    }

    mutation.mutate(cardData)
  }

  return (
    <Fade in={true} timeout={300}>
      <Container maxWidth="sm" sx={{ py: 3 }}>
        <Card elevation={3} sx={{ borderRadius: 2, overflow: "visible" }}>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CreditCardIcon color="primary" />
                <Typography variant="h5" fontWeight="bold">
                  Create New Loyalty Card
                </Typography>
              </Box>
            }
            subheader="Add a new customer loyalty card to the system"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              borderBottom: "1px solid",
              borderColor: "divider",
              pb: 2,
            }}
          />

          <CardContent sx={{ p: 3 }}>
            {validationError && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setValidationError(null)}>
                {validationError}
              </Alert>
            )}

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.05),
                  border: "1px solid",
                  borderColor: alpha(theme.palette.info.main, 0.2),
                  borderRadius: 1.5,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <InfoIcon color="info" fontSize="small" />
                  <Typography variant="subtitle2" fontWeight="medium">
                    Customer Identification
                  </Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Choose how to identify the customer for this loyalty card
                </Typography>

                <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                  <InputLabel id="select-label">Identification Type</InputLabel>
                  <Select
                    labelId="select-label"
                    value={inputType}
                    onChange={(e) => setInputType(e.target.value)}
                    label="Identification Type"
                    startAdornment={
                      <InputAdornment position="start" sx={{ ml: 1, mr: -0.5 }}>
                        {inputType === "phone" ? (
                          <PhoneIcon color="primary" fontSize="small" />
                        ) : (
                          <BadgeIcon color="secondary" fontSize="small" />
                        )}
                      </InputAdornment>
                    }
                  >
                    <MenuItem value="phone">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <PhoneIcon sx={{ mr: 1 }} color="primary" fontSize="small" />
                        Phone Number
                      </Box>
                    </MenuItem>
                    <MenuItem value="personal-code">
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <BadgeIcon sx={{ mr: 1 }} color="secondary" fontSize="small" />
                        Personal Code
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label={inputType === "phone" ? "Phone Number" : "Personal Code"}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  variant="outlined"
                  error={!!validationError && validationError.includes(inputType === "phone" ? "phone" : "code")}
                  helperText={
                    inputType === "phone"
                      ? "Format: +2135XXXXXXXX, +2137XXXXXXXX, 05XXXXXXXX, or 07XXXXXXXX"
                      : "Enter customer's unique personal code"
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {inputType === "phone" ? <PhoneIcon color="action" /> : <BadgeIcon color="action" />}
                      </InputAdornment>
                    ),
                  }}
                />
              </Paper>

              <Divider>
                <Chip label="Card Details" variant="outlined" size="small" />
              </Divider>

              <TextField
                fullWidth
                label="Card Label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                variant="outlined"
                placeholder="Enter a descriptive label for this card"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LabelIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />

              <Box sx={{ position: "relative" }}>
                <TextField
                  fullWidth
                  label="Fidelity Code"
                  value={fidelityCode}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  placeholder="Generate a unique fidelity code"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <QrCodeIcon color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: fidelityCode && (
                      <InputAdornment position="end">
                        <Tooltip title="Regenerate code">
                          <IconButton
                            edge="end"
                            onClick={handleGenerateFidelityCode}
                            disabled={isGenerating}
                            size="small"
                          >
                            <RefreshIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                    readOnly: true,
                  }}
                />
                <Button
                  variant="contained"
                  onClick={handleGenerateFidelityCode}
                  disabled={isGenerating}
                  sx={{
                    position: "absolute",
                    right: 0,
                    top: 0,
                    height: "100%",
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    width: "120px",
                    boxShadow: "none",
                    display: !fidelityCode ? "flex" : "none",
                  }}
                  startIcon={<RefreshIcon />}
                >
                  {isGenerating ? "..." : "Generate"}
                </Button>
              </Box>

              {fidelityCode && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.05),
                    border: "1px solid",
                    borderColor: alpha(theme.palette.success.main, 0.2),
                    borderRadius: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexDirection: "column",
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Generated Fidelity Code
                  </Typography>
                  <Typography
                    variant="h5"
                    fontFamily="monospace"
                    fontWeight="bold"
                    color={theme.palette.success.dark}
                    sx={{ letterSpacing: 1 }}
                  >
                    {fidelityCode}
                  </Typography>
                </Paper>
              )}
            </Box>
          </CardContent>

          <Divider />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              p: 3,
              bgcolor: alpha(theme.palette.background.default, 0.5),
            }}
          >
            <Button
              variant="outlined"
              color="error"
              onClick={onCancel}
              startIcon={<CancelIcon />}
              sx={{ borderRadius: 1.5 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={mutation.isPending}
              startIcon={<SaveIcon />}
              sx={{ borderRadius: 1.5 }}
            >
              {mutation.isPending ? "Creating..." : "Create Card"}
            </Button>
          </Box>
        </Card>
      </Container>
    </Fade>
  )
}

export default CardEditor

